import React from "react";
import { extendObservable, runInAction, action, reaction } from "mobx";
import { post } from "../lib/http";
import { getRate } from "../lib/countryRate";
import { fakeProgressHack } from "../utils/fakeProgressHack";
import { appStore } from "./AppStore";

const RATE_SERVICE_SOURCE_NAME = "cm-onboarding";

export class RateStore {
  negotiateData = {};

  constructor() {
    extendObservable(
      this,
      {
        rateStatus: "",
        isDone: false,
        isSaving: false,
        isLoading: false,
        salary: "",
        rateAmount: 0,
        minProjectAmount: 0,
        rateAmountOriginal: 0,
        minProjectAmountOriginal: 0,
        rangeHigh: 0,
        rangeLow: 0,
        recommendationLoadingStatus: "",
        recommendationLoadingPercent: 0,
        approvalNeeded: false,
        negotiateNeeded: false,
        negotiateDeclined: false,
        suggestedNoApprovalNeeded: 0,
        recommendedRateByCountry: 0,
        recommendedRateByCountryDisplay: 0,
        localeInfo: {},
        countries: [],
        hidingSalary: false
      },
      null,
      { deep: false }
    );

    reaction(
      () => this.rateAmount,
      rate => {
        // update the minProjectAmount relative to the new rate
        // https://github.com/glg/rate-recommender/blob/6ddd73f62bd8708026d29cc9d2e617dd63dfe806/src/pages/Results.js#L47-L48
        if (this.minProjectAmountOriginal && this.rateAmountOriginal && rate) {
          this.minProjectAmount =
            Math.round(
              ((rate / this.rateAmountOriginal) *
                this.minProjectAmountOriginal) /
                5
            ) * 5;
        }
      }
    );
  }

  init = action((userData) => {
    this.rateAmount = this.rateAmountOriginal = userData.rateAmount || 0;
    this.minProjectAmount = this.minProjectAmountOriginal = userData.minProjectAmount || 0;
    //if we're reloading the page after the user has been created on the first page, use the saved country code to prepopulate rate suggestion
    if (userData.country) {
      this.getRecommendedRateByCountry(userData.country, userData.currency);
    }
  })

    //hiding salary info for users in US and seeing English text
    shouldHideSalary = (countryCode, locale) => {
      console.log("Got country code of " + countryCode + " and locale of " + locale)
      this.localeInfo = { countryCode, locale, zoneRegion: appStore.zoneRegion};
      const languageOnly = locale.substring(0,2) // locales can be something like 'en-US' or 'en-AUS', we just want to know the language
      // hide salary for JPY NM
      this.hidingSalary = (["US/Canada", "Europe", "Africa & Middle East"].includes(appStore.zoneRegion) && ['en', 'it', 'de', 'pt', 'es', 'fr', 'ru'].includes(languageOnly)) || ["JPY"].includes(appStore.currency);

      return this.hidingSalary;
    }

  getRecommendedRateByCountry = (countryCode, currency) => {
    const rateAmountByCountry = getRate(countryCode, currency);
    if (rateAmountByCountry && this.shouldHideSalary(countryCode, appStore.locale)) {
      this.rateAmount = this.recommendedRateByCountryDisplay = rateAmountByCountry;
      this.recommendedRateByCountry = getRate(countryCode);
    }
  }

  getNegotiated = action(() => {
    this.isLoading = true;
    return post(`api/rate/negotiate/get`, { sourceName: RATE_SERVICE_SOURCE_NAME, localeInfo: this.localeInfo })
      .then(results =>
        runInAction(() => {

          this.isLoading = false;
          this.rateStatus = results.status;
          this.approvalNeeded = ["Requested", "Approver Requested", "Reason Requested"].includes(results.status);
          this.negotiateNeeded = results.status === "Negotiate";
          this.isDone = results.status !== "New";
          if (results.status === "Negotiate") {
            this.isDone = false;
            this.negotiateNeeded = true;
            this.rateAmount = results.rate;
            this.rangeHigh = results.rangeHigh;
            this.rangeLow = results.rangeLow;
            this.negotiateData = results;
          }
        })
    );
  });

  setNegotiated = action(() => {
    this.isSaving = true;
    let newRate = {
      rateAmount: this.rateAmount,
      minProjectAmount: this.minProjectAmount,
      sourceName: RATE_SERVICE_SOURCE_NAME
    };
    let rateAmount = Object.assign({}, this.negotiateData, newRate);
    return post(`api/rate/update/setNegotiated`, { rateAmount, localeInfo: this.localeInfo })
      .then(results =>
        runInAction(() => {
          this.isSaving = false;
          this.isDone = true;
          this.approvalNeeded = results.approvalNeeded;
        })
    );
  });

  declineNegotiated = action(() => {
    this.isSaving = true;
    return post(
      `api/rate/negotiate/decline`, {localeInfo: this.localeInfo})
      .then(() =>
        runInAction(() => {
          this.isSaving = false;
          this.isDone = true;
          this.negotiateDeclined = true;
        })
      );
  });

  getRecommended = action(() => {
    if (this.recommendationLoadingStatus === "loading") {
      return Promise.resolve();
    }
    const alreadyRan = this.recommendationLoadingStatus === "loaded";
    this.recommendationLoadingStatus = "loading";
    const salary = this.salary;

    const recommendation = alreadyRan
      ? Promise.resolve()
      : post(`api/rate/recommend/get`, { salary, sourceName: RATE_SERVICE_SOURCE_NAME, localeInfo: this.localeInfo });

    const [fakeProgressPromise] = fakeProgressHack(
      value => runInAction(() => (this.recommendationLoadingPercent = value)),
      alreadyRan ? 1000 : 3000
    );

    return Promise.all([recommendation, fakeProgressPromise])
      .then(results =>
        runInAction(() => {
          if (!alreadyRan) {
            const rec = results[0];
            if (!rec || !rec.rate) {
              throw new Error("no rate given");
            }
            this.rangeLow = rec.rangeLow;
            this.rangeHigh = rec.rangeHigh;
            this.rateAmount = rec.rate;
          }
          this.recommendationLoadingStatus = "loaded";
        })
      )
      .catch(e =>
        runInAction(() => {
          this.recommendationLoadingStatus = "";
          return Promise.reject(e);
        })
      );
  });

  checkApproval = action(() => {
    this.isSaving = true;
    const salary = this.salary;
    const payload = {
      salary,
      rateAmount: this.rateAmount,
      recommendedRateByCountry: this.recommendedRateByCountry,
      sourceName: RATE_SERVICE_SOURCE_NAME,
      localeInfo: this.localeInfo,
      currency: appStore.currency
    };
    return post(`api/rate/update/test`, payload)
      .then(results =>
        runInAction(() => {
          this.isSaving = false;
          this.approvalNeeded = results.approvalNeeded;
          this.suggestedNoApprovalNeeded =
            results.suggestedNoApprovalNeeded || 0;
          console.log(results);
          if (!this.approvalNeeded) {
            return this.updateRate();
          }

          // for JP NM it will directly sent to ask for approval
          if (this.approvalNeeded && appStore.country == 962) {
            return this.updateRate();
          }
        })
      )
      .catch(e =>
        runInAction(() => {
          this.isSaving = false;
          throw e;
        })
      );
  });

  acceptSuggested = action(() => {
    this.rateAmount = this.suggestedNoApprovalNeeded;
    return this.updateRate();
  });

  updateRate = action(() => {
    this.isSaving = true;
    const salary = this.salary;
    const payload = {
      salary,
      rateAmount: this.rateAmount,
      minProjectAmount: this.minProjectAmount,
      recommendedRateByCountry: this.recommendedRateByCountry,
      localeInfo: this.localeInfo,
      sourceName: RATE_SERVICE_SOURCE_NAME,
      currency: appStore.currency
    };
    console.log(payload)
    return post(`api/rate/update/set`, payload)
      .then(results =>
        runInAction(() => {
          this.isSaving = false;
          this.isDone = true;
          this.approvalNeeded = results.approvalNeeded;
        })
      )
      .catch(e =>
        runInAction(() => {
          this.isSaving = false;
          throw e;
        })
      );
  });
}

export const rateStore = new RateStore();
export const RateStoreContext = React.createContext();
